import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { FaRegEdit } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import { AuthService, fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';
import pkg from '../../package.json';
const Profile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({});
  const [dash, setDash] = useState({});
  const [showPic, setShowPic] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [profileImage, setProfileImage] = useState(null);

  function goto(add) {
    navigate(add);
  }

  // Fetch profile info
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('tringAuthToken');
      if (!token) return;

      try {
        const response = await fetchWithAuth(API_ENDPOINTS.USERS, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        setProfile(data);
        setSelectedImage(data?.avatar);
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };
    fetchProfile();
  }, []);

  // Fetch dashboard info
  useEffect(() => {
    const fetchDash = async () => {
      const token = localStorage.getItem('tringAuthToken');
      if (!token) return;
      try {
        const response = await fetchWithAuth(API_ENDPOINTS.USER_DASHBOARD, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        setDash(data);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
      }
    };
    fetchDash();
  }, []);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('tringAuthToken');
    if (!token) {
      navigate('/');
    }
  }, [navigate]);

  const signOut = () => {
    AuthService.clearTokens();
    localStorage.clear(); // Clears both localStorage and sessionStorage
    navigate('/');
  };

  function cancelShowPic() {
    setShowPic(false);
  }
  function openPic() {
    setShowPic(true);
  }

  // Avatar upload function (returns avatar url)
  const uploadImage = async file => {
    const formData = new FormData();
    formData.append('avatar', file);
    const token = localStorage.getItem('tringAuthToken');
    try {
      const response = await fetch(API_ENDPOINTS.USER_UPLOAD_AVATAR, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload image');
      }
      const data = await response.json();
      // API returns avatar_url
      console.log('Image uploaded successfully:', data);
      setAvatarUrl(data.avatar);
    } catch (error) {
      console.error('Image Upload Error:', error);
      throw error;
    }
  };

  const handleAvatarChange = async e => {
    const file = e.target.files[0];
    if (file) {
      setAvatarUploading(true);
      try {
        // Optional: show preview immediately
        setEditForm(prev => ({ ...prev, avatar: URL.createObjectURL(file) }));

        const avatarUrl = await uploadImage(file);
        setEditForm(prev => ({ ...prev, avatar: avatarUrl }));
        setAvatarUrl(avatarUrl); // <-- use the returned value here
      } catch (error) {
        toast.error(error.message || 'Failed to upload image');
      } finally {
        setAvatarUploading(false);
      }
    }
  };

  // Show edit modal and prefill form
  const handleEditOpen = () => {
    setEditForm({
      avatar: profile.avatar || '',
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      gender: profile.gender || '',
      phone_number: profile.phone_number || '',
      email: profile.email || '',
    });
    setShowEditModal(true);
  };

  // Handle edit form changes
  const handleEditChange = e => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Save profile (PUT)
  const handleEditSubmit = async e => {
    e.preventDefault();
    const token = localStorage.getItem('tringAuthToken');
    if (!token) return;
    // Only send editable fields + avatar url
    const payload = {
      avatar_url: editForm.avatar,
      first_name: editForm.first_name,
      last_name: editForm.last_name,
      gender: editForm.gender,
      phone_number: editForm.phone_number,
      email: editForm.email,
    };
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.USERS, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to update profile');
      const data = await response.json();
      setProfile(data);
      setSelectedImage(data?.avatar);
      setShowEditModal(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    }
  };

  // Show modal for avatar only
  const handleImageChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProfileImage(URL.createObjectURL(file));
    const token = localStorage.getItem('tringAuthToken');
    const formDataImg = new FormData();
    formDataImg.append('avatar', file);

    try {
      const response = await fetchWithAuth(
        API_ENDPOINTS.PARTNER_UPLOAD_AVATAR,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataImg,
        }
      );

      if (!response.ok) throw new Error('Failed to upload avatar');
      const data = await response.json();
      setAvatarUrl(data.avatar_url || data.avatar); // <-- fix here
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    }
  };

  // Color for headings and buttons
  const accentColor = 'linear-gradient(90deg, #CC151E 0.27%, #863664 99.73%)';
  const accentTextColor = '#fff';
  const headingStyle = {
    fontSize: 14,
    color: accentColor,
    fontWeight: 600,
    marginBottom: 2,
    textAlign: 'left',
    display: 'block',
  };

  return (
    <div
      className="d-flex flex-column align-items-center profile-container mb-5"
      style={{ height: '93dvh', overflowY: 'scroll', scrollBehavior: 'smooth' }}
    >
      <ToastContainer />
      {/* Avatar modal */}
      {showPic ? (
        <div className="picmodale">
          <div className="picoverlay" onClick={cancelShowPic}></div>
          <b>
            <IoMdCloseCircleOutline
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                zIndex: 3000,
                fontSize: '35px',
              }}
              onClick={cancelShowPic}
            />
          </b>
          <div className="picmodalecontent d-flex flex-column align-items-center">
            <img
              src={selectedImage}
              alt="avatar"
              style={{
                borderRadius: '50%',
                width: '65%',
                aspectRatio: '1/1',
                marginBottom: '10px',
              }}
            />
            <input
              type="file"
              style={{ width: '50%' }}
              accept="image/*"
              onChange={handleImageChange}
            />
          </div>
        </div>
      ) : null}

      {/* Edit profile modal */}
      {showEditModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 2000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <form
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              minWidth: 320,
              maxWidth: 380,
              width: '90vw',
              position: 'relative',
            }}
            onSubmit={handleEditSubmit}
          >
            <IoMdCloseCircleOutline
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontSize: 32,
                cursor: 'pointer',
              }}
              onClick={() => setShowEditModal(false)}
            />
            <h3
              style={{
                marginBottom: 18,
                color: '#CC151E',
                textAlign: 'left',
                fontWeight: 700,
                fontSize: 22,
              }}
            >
              Edit Profile
            </h3>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  width: 86,
                  height: 86,
                  borderRadius: '50%',
                  background:
                    'linear-gradient(90deg, #CF141C 0%, #853765 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 8,
                  position: 'relative',
                }}
              >
                <img
                  src={editForm.avatar || 'https://placehold.co/80x80'}
                  alt="avatar"
                  style={{
                    width: 76,
                    height: 76,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    background: '#fff',
                  }}
                />
                <label
                  htmlFor="avatarEditUpload"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    background: '#fff',
                    borderRadius: '50%',
                    padding: 3,
                    cursor: 'pointer',
                    border: '1px solid #eee',
                  }}
                  title="Change avatar"
                >
                  <FaRegEdit style={{ fontSize: 18, color: '#CC151E' }} />
                  <input
                    id="avatarEditUpload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleAvatarChange}
                  />
                </label>
                {avatarUploading && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(255,255,255,0.7)',
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: 12,
                    }}
                  >
                    Uploading...
                  </div>
                )}
              </div>
            </div>
            <div className="form-group mb-2" style={{ textAlign: 'left' }}>
              <label style={headingStyle}>First Name</label>
              <input
                type="text"
                name="first_name"
                value={editForm.first_name}
                onChange={handleEditChange}
                className="form-control"
                style={{ width: '100%', marginBottom: 8 }}
                required
                maxLength={10}
              />
            </div>

            <div className="form-group mb-2" style={{ textAlign: 'left' }}>
              <label style={headingStyle}>Last Name</label>
              <input
                type="text"
                name="last_name"
                value={editForm.last_name}
                onChange={handleEditChange}
                className="form-control"
                style={{ width: '100%', marginBottom: 8 }}
                maxLength={10}
              />
            </div>

            <div className="form-group mb-2" style={{ textAlign: 'left' }}>
              <label style={headingStyle}>Gender</label>
              <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                <label style={{ color: '#7a7a7a', fontWeight: 500 }}>
                  <input
                    type="radio"
                    name="gender"
                    value="Male"
                    checked={editForm.gender === 'Male'}
                    onChange={handleEditChange}
                  />{' '}
                  Male
                </label>
                <label style={{ color: '#7a7a7a', fontWeight: 500 }}>
                  <input
                    type="radio"
                    name="gender"
                    value="Female"
                    checked={editForm.gender === 'Female'}
                    onChange={handleEditChange}
                  />{' '}
                  Female
                </label>
              </div>
            </div>
            <div className="form-group mb-2" style={{ textAlign: 'left' }}>
              <label style={headingStyle}>Phone Number</label>
              <input
                type="tel"
                name="phone_number"
                value={editForm.phone_number}
                className="form-control"
                style={{ width: '100%', marginBottom: 8 }}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-group mb-2" style={{ textAlign: 'left' }}>
              <label style={headingStyle}>Email</label>
              <input
                type="email"
                name="email"
                value={editForm.email}
                className="form-control"
                style={{ width: '100%', marginBottom: 8 }}
                onChange={handleEditChange}
              />
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginTop: 18,
                justifyContent: 'flex-end',
              }}
            >
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                style={{
                  background: accentColor,
                  color: accentTextColor,
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 22px',
                  fontWeight: 600,
                  fontSize: 15,
                  marginRight: 8,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={avatarUploading}
                style={{
                  background: accentColor,
                  color: accentTextColor,
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 22px',
                  fontWeight: 600,
                  fontSize: 15,
                }}
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Profile Header */}
      <div
        className="px-3 py-3 mt-2 d-flex flex-row text-start position-relative"
        style={{
          width: '90%',
          height: '17vh',
          background: 'linear-gradient(90deg, #CF141C 0%, #853765 100%)',
          borderRadius: '13px',
          gap: '15px',
        }}
      >
        <div onClick={openPic} style={{ cursor: 'pointer' }}>
          <img
            src={selectedImage}
            alt="avatar"
            style={{
              height: '13vh',
              borderRadius: '50%',
              width: '13vh',
              objectFit: 'cover',
            }}
          />
        </div>
        <div className="mb-3" style={{ marginTop: '4vh' }}>
          <span
            className="common-txt"
            style={{
              color: '#FFF',
              fontSize: 'clamp(0px,3.8vw,16px)',
              fontWeight: 600,
            }}
          >
            {profile.first_name} <br />
          </span>

          <span
            className="common-txt"
            style={{ color: '#FFF', fontWeight: 540 }}
          >
            <span>{profile.phone_number}</span>
          </span>
        </div>
        <img
          src="images/editprofile.svg"
          className="position-absolute"
          style={{ top: '16px', right: '14px', cursor: 'pointer' }}
          alt="edit"
          onClick={handleEditOpen}
        />
      </div>

      {/* Dashboard Info */}
      <div
        className="d-flex flex-row justify-content-between"
        style={{ width: '90%' }}
      >
        <div
          className="d-flex flex-column align-items-center"
          style={{ width: '30%' }}
        >
          <div
            className="w-75"
            style={{ border: 'solid 1px #B2B2B2', height: '2vh' }}
          ></div>
          <div
            className="p-2 w-100"
            style={{
              background:
                'linear-gradient(90deg, #CC151E 0.27%, #863664 99.73%)',
              borderRadius: '13px',
            }}
          >
            <span
              className="common-txt d-flex flex-row align-items-center justify-content-center"
              style={{
                color: '#FFF',
                fontSize: 'clamp(0px,4vw,16px)',
                fontWeight: 600,
              }}
            >
              <img src="images/profilei1.svg" className="mx-1" alt="c1" />{' '}
              {dash?.checkIns}
              <br />
            </span>
            <span
              className="common-txt mt-1"
              style={{
                color: '#FFF',
                fontWeight: 600,
                fontSize: 'clamp(0px,3.5vw,14px)',
                whiteSpace: 'nowrap',
              }}
            >
              Check-Ins
            </span>
          </div>
        </div>

        <div
          className="d-flex flex-column align-items-center"
          style={{ width: '30%' }}
        >
          <div
            className="w-75"
            style={{ border: 'solid 1px #B2B2B2', height: '2vh' }}
          ></div>
          <div
            className="p-2 w-100"
            style={{
              background:
                'linear-gradient(90deg, #CC151E 0.27%, #863664 99.73%)',
              borderRadius: '13px',
            }}
          >
            <span
              className="common-txt d-flex flex-row align-items-center justify-content-center"
              style={{
                color: '#FFF',
                fontSize: 'clamp(0px,4vw,16px)',
                fontWeight: 600,
              }}
            >
              <img src="images/profilei2.svg" className="mx-1" alt="c2" />{' '}
              {dash?.songsPlayed}
              <br />
            </span>
            <span
              className="common-txt mt-1"
              style={{
                color: '#FFF',
                fontWeight: 600,
                fontSize: 'clamp(0px,3.5vw,14px)',
                whiteSpace: 'nowrap',
              }}
            >
              Songs Played
            </span>
          </div>
        </div>

        <div
          className="d-flex flex-column align-items-center"
          style={{ width: '30%' }}
        >
          <div
            className="w-75"
            style={{ border: 'solid 1px #B2B2B2', height: '2vh' }}
          ></div>
          <div
            className="p-2 w-100"
            style={{
              background:
                'linear-gradient(90deg, #CC151E 0.27%, #863664 99.73%)',
              borderRadius: '13px',
            }}
          >
            <span
              className="common-txt d-flex flex-row align-items-center justify-content-center"
              style={{
                color: '#FFF',
                fontSize: 'clamp(0px,4vw,16px)',
                fontWeight: 600,
              }}
            >
              <img src="images/profilei3.svg" className="mx-1" alt="c3" />{' '}
              {dash?.shoutOuts}
              <br />
            </span>
            <span
              className="common-txt mt-1"
              style={{
                color: '#FFF',
                fontWeight: 600,
                fontSize: 'clamp(0px,3.5vw,14px)',
                whiteSpace: 'nowrap',
              }}
            >
              Shout-Outs
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="text-start" style={{ width: '90%', marginTop: '28px' }}>
        <button
          className="common-txt my-1 d-flex align-items-center w-100"
          onClick={() => goto('/buy')}
          style={{
            color: '#4C4C4C',
            border: 'none',
            background: 'transparent',
            fontSize: 'clamp(0px,3.7vw,15px)',
            fontWeight: 600,
          }}
        >
          <img
            style={{ height: '1.5rem', marginRight: '25px' }}
            src="images/profilel1.svg"
            alt=""
          />{' '}
          Buy Coins
        </button>

        <hr />

        <button
          className="common-txt my-3 d-flex align-items-center w-100"
          style={{
            color: '#4C4C4C',
            border: 'none',
            background: 'transparent',
            fontSize: 'clamp(0px,3.7vw,15px)',
            fontWeight: 600,
          }}
          onClick={() => goto('/support')}
        >
          <img
            style={{ height: '1.5rem', marginRight: '25px' }}
            src="images/profilel2.svg"
            alt=""
          />{' '}
          Support
        </button>

        <hr />

        <button
          className="common-txt my-3 d-flex align-items-center w-100"
          onClick={() => goto('/history')}
          style={{
            color: '#4C4C4C',
            border: 'none',
            background: 'transparent',
            fontSize: 'clamp(0px,3.7vw,15px)',
            fontWeight: 600,
          }}
        >
          <img
            style={{ height: '1.5rem', marginRight: '25px' }}
            src="images/profilel3.svg"
            alt=""
          />{' '}
          Coins History
        </button>

        <hr />

        <button
          className="common-txt my-3 d-flex align-items-center w-100"
          onClick={() => goto('/ChanceHistory')}
          style={{
            color: '#4C4C4C',
            border: 'none',
            background: 'transparent',
            fontSize: 'clamp(0px,3.7vw,15px)',
            fontWeight: 600,
          }}
        >
          <img
            style={{ height: '1.5rem', marginRight: '25px' }}
            src="images/nav-chance.svg"
            alt=""
          />{' '}
          Chance History
        </button>

        <hr />

        <button
          className="common-txt mb-3 mt-3 d-flex align-items-center w-100"
          style={{
            color: '#4C4C4C',
            border: 'none',
            background: 'transparent',
            fontSize: 'clamp(0px,3.7vw,15px)',
            fontWeight: 600,
          }}
          onClick={() => goto('/redeem')}
        >
          <span
            style={{ height: '1.5rem', width: 'auto', marginRight: '25px' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 72 76"
              fill="none"
              style={{ height: '1.5rem', verticalAlign: 'middle' }}
            >
              <path
                d="M7.33317 59.5835V67.4168H64.6665V59.5835H7.33317ZM7.33317 16.5002H15.2165C14.9179 15.9127 14.7238 15.2925 14.6342 14.6397C14.5446 13.987 14.4998 13.3016 14.4998 12.5835C14.4998 9.31961 15.545 6.5453 17.6353 4.26058C19.7255 1.97586 22.2637 0.833496 25.2498 0.833496C27.0415 0.833496 28.6988 1.3394 30.2217 2.3512C31.7446 3.36301 33.0734 4.61961 34.2082 6.121L35.9998 8.66683L37.7915 6.121C38.8665 4.55433 40.1804 3.28141 41.7332 2.30225C43.2859 1.32308 44.9582 0.833496 46.7498 0.833496C49.7359 0.833496 52.2741 1.97586 54.3644 4.26058C56.4547 6.5453 57.4998 9.31961 57.4998 12.5835C57.4998 13.3016 57.455 13.987 57.3655 14.6397C57.2759 15.2925 57.0818 15.9127 56.7832 16.5002H64.6665C66.6373 16.5002 68.3245 17.2672 69.728 18.8012C71.1314 20.3352 71.8332 22.1793 71.8332 24.3335V67.4168C71.8332 69.571 71.1314 71.4151 69.728 72.9491C68.3245 74.4831 66.6373 75.2502 64.6665 75.2502H7.33317C5.36234 75.2502 3.67518 74.4831 2.27171 72.9491C0.86824 71.4151 0.166504 69.571 0.166504 67.4168V24.3335C0.166504 22.1793 0.86824 20.3352 2.27171 18.8012C3.67518 17.2672 5.36234 16.5002 7.33317 16.5002ZM7.33317 47.8335H64.6665V24.3335H46.3915L53.9165 35.496L48.1832 40.0002L35.9998 21.9835L23.8165 40.0002L18.0832 35.496L25.429 24.3335H7.33317V47.8335ZM25.2498 16.5002C26.2651 16.5002 27.1162 16.1248 27.803 15.3741C28.4898 14.6234 28.8332 13.6932 28.8332 12.5835C28.8332 11.4738 28.4898 10.5436 27.803 9.79287C27.1162 9.04218 26.2651 8.66683 25.2498 8.66683C24.2346 8.66683 23.3835 9.04218 22.6967 9.79287C22.0099 10.5436 21.6665 11.4738 21.6665 12.5835C21.6665 13.6932 22.0099 14.6234 22.6967 15.3741C23.3835 16.1248 24.2346 16.5002 25.2498 16.5002ZM46.7498 16.5002C47.7651 16.5002 48.6162 16.1248 49.303 15.3741C49.9898 14.6234 50.3332 13.6932 50.3332 12.5835C50.3332 11.4738 49.9898 10.5436 49.303 9.79287C48.6162 9.04218 47.7651 8.66683 46.7498 8.66683C45.7346 8.66683 44.8835 9.04218 44.1967 9.79287C43.5099 10.5436 43.1665 11.4738 43.1665 12.5835C43.1665 13.6932 43.5099 14.6234 44.1967 15.3741C44.8835 16.1248 45.7346 16.5002 46.7498 16.5002Z"
                fill="url(#paint0_linear)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear"
                  x1="35.9998"
                  y1="0.833496"
                  x2="35.9998"
                  y2="75.2502"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#B62136" />
                  <stop offset="1" stopColor="#992E52" />
                </linearGradient>
              </defs>
            </svg>
          </span>
          Redeem Promo Code
        </button>

        <hr />

        <button
          className="common-txt mb-3 mt-3 d-flex align-items-center w-100"
          style={{
            color: '#4C4C4C',
            border: 'none',
            background: 'transparent',
            fontSize: 'clamp(0px,3.7vw,15px)',
            fontWeight: 600,
          }}
          onClick={signOut}
        >
          <img
            style={{ height: '1.5rem', marginRight: '25px' }}
            src="images/profilel6.svg"
            alt=""
          />{' '}
          Log Out
        </button>
      </div>

      <div className="mt-3 w-100">
        <img
          className=""
          style={{ width: '90%' }}
          src="images/profilelast.png"
          alt=""
        />
      </div>
      <p style={{ color: 'gray', marginBottom: '10vh' }}>v{pkg.version}</p>
    </div>
  );
};

export default Profile;
